import React from "react";
import { t } from "i18next";
import { useDispatch } from "react-redux";
import useTheme from "../../../../hooks/useTheme";
import { withOpacity, getLocale, getTimeStamp } from "../../../../utils/helperFunctions";
import { MODAL_CANDIDATE_DETAIL } from "../../../Candidate/CandidateDetail/CandidateViewPage";
import { WorkplaceLogo } from "../../../UI/DisplayImage";
import { UserAvatar } from "../../../UI/DisplayImage";
import { AppRow, AppColumn } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { SizedBox } from "../../../UI/Sizes";
import { Icon } from "semantic-ui-react";
import { showToast } from "../../../../redux/Snackbar/SnackbarActions";

const ACCENT_COLORS = [
  "#6366f1", // indigo
  "#0891b2", // cyan
  "#059669", // emerald
  "#d97706", // amber
  "#e11d48", // rose
  "#7c3aed", // violet
  "#0284c7", // sky
  "#16a34a", // green
];

const formatJobDate = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const locale =
    typeof navigator !== "undefined"
      ? ((navigator.language || navigator.userLanguage) ?? "sv")
      : "sv";
  const useLocale = locale.startsWith("sv") ? "sv-SE" : "en-GB";
  return new Intl.DateTimeFormat(useLocale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const JobSeparator = ({
  profession,
  timestamp,
  jobId,
  appliedJobs,
  openModal,
  coreConstants,
  workplaceName,
  healthcareWorkplaceName,
  referenceNumber,
  accentIndex = 0,
  recentReplies = [],
  totalReplyCount = 0,
  unreadCount = 0,
  profilesData = {},
  onReplyTap,
  onViewAllReplies,
  children,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const displayWorkplaceName = healthcareWorkplaceName || workplaceName;
  const accent = ACCENT_COLORS[accentIndex % ACCENT_COLORS.length];

  // Own tap target, nested inside the header's own onClick (opens the
  // candidate modal) — stopPropagation keeps it from also triggering that.
  const handleCopyReference = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!referenceNumber || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(referenceNumber);
    dispatch(showToast(t("chat.reference_number_copied"), { type: "success" }));
  };

  // Resolve translated profession name
  const roles = coreConstants?.workroles ?? [];
  const professionText =
    roles.find((r) => r.id === profession)?.text?.[getLocale()] ?? profession;

  // Find matching application for logo and city
  const matchingApp = appliedJobs?.find(
    (job) =>
      job.jobDetails?.jobId === jobId ||
      job.jobDetails?.shareableJobId === jobId ||
      job.jobId === jobId,
  );
  const logoUrl = matchingApp?.jobDetails?.workplaceLogo;
  const city =
    matchingApp?.jobDetails?.workplaceCity ||
    matchingApp?.jobDetails?.workplaceMunicipality;

  const isClickable = jobId && appliedJobs && openModal && matchingApp;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isClickable) return;
    openModal(MODAL_CANDIDATE_DETAIL, {
      candidateData: matchingApp,
      jobId: matchingApp.jobDetails?.jobId ?? matchingApp.jobId,
    });
  };

  return (
    <div
      style={{
        marginTop: theme.spacing.elementPadding,
        marginBottom: theme.spacing.elementPaddingSmall,
        borderRadius: theme.radius.default,
        border: `1px solid ${theme.colors.lightGrey}`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        onClick={isClickable ? handleClick : undefined}
        style={{
          padding: `${theme.spacing.elementPadding} 1.5rem`,
          background: `linear-gradient(135deg, ${withOpacity(accent, "0.08")} 0%, ${withOpacity(accent, "0.03")} 100%)`,
          borderBottom: `1px solid ${theme.colors.lightGrey}`,
          cursor: isClickable ? "pointer" : "default",
        }}
      >
        <AppRow crossAxisAlignment="center" style={{ gap: "0.75rem" }}>
          <WorkplaceLogo
            url={logoUrl}
            style={{
              height: "44px",
              width: "44px",
              flexShrink: 0,
            }}
            borderRadius="10px"
          />
          <AppColumn style={{ flex: 1, minWidth: 0 }}>
            <AppText.Caption fontWeight={600}>
              {professionText}
            </AppText.Caption>
            {displayWorkplaceName && (
              <AppText.FootnoteSmall color={theme.colors.grey}>
                {displayWorkplaceName}
                {city ? ` \u00B7 ${city}` : ""}
              </AppText.FootnoteSmall>
            )}
          </AppColumn>
          <AppColumn
            crossAxisAlignment="end"
            style={{ flexShrink: 0 }}
          >
            {referenceNumber && (
              <AppText.FootnoteSmall
                color={theme.colors.grey}
                cursor="pointer"
                onClick={handleCopyReference}
              >
                #{referenceNumber} <Icon name="copy outline" style={{ fontSize: "0.85em" }} />
              </AppText.FootnoteSmall>
            )}
            <AppText.FootnoteSmall color={theme.colors.grey}>
              {formatJobDate(timestamp)}
            </AppText.FootnoteSmall>
          </AppColumn>
          {unreadCount > 0 && (
            <div
              style={{
                backgroundColor: accent,
                color: theme.colors.white,
                borderRadius: "10px",
                padding: "2px 8px",
                fontSize: "11px",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {t("chat.new_count", { count: unreadCount })}
            </div>
          )}
          {isClickable && (
            <Icon
              name="chevron right"
              style={{
                color: accent,
                fontSize: "0.9rem",
                margin: 0,
                flexShrink: 0,
              }}
            />
          )}
        </AppRow>
      </div>

      {/* Messages */}
      {children && (
        <div
          style={{
            padding: `${theme.spacing.elementPadding} 1.5rem`,
            backgroundColor: theme.colors.white,
          }}
        >
          {children}
        </div>
      )}

      {/* Inline thread replies */}
      {recentReplies.length > 0 && (
        <div
          style={{
            borderTop: `1px solid ${theme.colors.lightGrey}`,
            backgroundColor: withOpacity(theme.colors.lightGrey, "0.3"),
          }}
        >
          {/* Reply divider */}
          <AppRow
            crossAxisAlignment="center"
            style={{
              padding: `${theme.spacing.elementPadding} 1.5rem`,
              gap: "0.5rem",
            }}
          >
            <div style={{ flex: 1, height: "1px", backgroundColor: theme.colors.lightGrey }} />
            <AppText.FootnoteSmall color={theme.colors.grey} fontWeight={500}>
              {t(totalReplyCount === 1 ? "chat.reply" : "chat.replies", { count: totalReplyCount })}
            </AppText.FootnoteSmall>
            <div style={{ flex: 1, height: "1px", backgroundColor: theme.colors.lightGrey }} />
          </AppRow>

          {/* Recent replies rendered inline */}
          <div style={{ padding: "0 1.5rem" }}>
            {recentReplies.map((reply) => {
              const senderId = reply?.userId || reply?.senderId;
              const profile = profilesData?.[senderId];
              const name = profile?.name || "";
              const text = reply?.contentData?.text || reply?.content || "";
              const isFile = reply?.contentData?.type === "file";

              return (
                <div
                  key={reply.messageId}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.5rem",
                    padding: `${theme.spacing.elementPadding} 0`,
                  }}
                >
                  <UserAvatar
                    name={name}
                    url={profile?.profileImageUrl}
                    style={{ height: "24px", width: "24px", flexShrink: 0, marginTop: "2px" }}
                  />
                  <AppColumn style={{ flex: 1, minWidth: 0 }}>
                    <AppRow crossAxisAlignment="center" style={{ gap: "0.5rem" }}>
                      <AppText.FootnoteSmall fontWeight={600}>
                        {name}
                      </AppText.FootnoteSmall>
                      <AppText.FootnoteSmall color={theme.colors.grey} style={{ fontSize: "10px" }}>
                        {getTimeStamp(reply.createdAt)}
                      </AppText.FootnoteSmall>
                    </AppRow>
                    <AppText.FootnoteSmall
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {isFile ? (
                        <span>
                          <Icon name="file alternate outline" style={{ fontSize: "11px" }} />
                          {reply?.contentData?.metaData?.fileData?.fileName || "File"}
                        </span>
                      ) : text}
                    </AppText.FootnoteSmall>
                  </AppColumn>
                </div>
              );
            })}
          </div>

          {/* Footer: View all + Reply */}
          <AppRow
            crossAxisAlignment="center"
            style={{
              padding: `${theme.spacing.elementPaddingSmall} 1.5rem ${theme.spacing.elementPadding}`,
              gap: "0.75rem",
            }}
          >
            {totalReplyCount > recentReplies.length && (
              <AppText.FootnoteSmall
                color={theme.colors.grey}
                cursor="pointer"
                onClick={onViewAllReplies}
                style={{ textDecoration: "underline" }}
              >
                {t("chat.view_all_replies", { count: totalReplyCount })}
              </AppText.FootnoteSmall>
            )}
            <div style={{ flex: 1 }} />
            <div
              onClick={onReplyTap}
              style={{
                cursor: "pointer",
                padding: "4px 12px",
                borderRadius: "6px",
                backgroundColor: withOpacity(theme.primaryColor, "0.1"),
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Icon name="reply" style={{ color: theme.primaryColor, fontSize: "12px", margin: 0 }} />
              <AppText.FootnoteSmall color={theme.primaryColor} fontWeight={600}>
                {t("chat.reply_action")}
              </AppText.FootnoteSmall>
            </div>
          </AppRow>
        </div>
      )}

      {/* No replies yet - show reply prompt */}
      {recentReplies.length === 0 && onReplyTap && (
        <div
          style={{
            borderTop: `1px solid ${theme.colors.lightGrey}`,
            padding: `${theme.spacing.elementPaddingSmall} 1.5rem`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={onReplyTap}
            style={{
              cursor: "pointer",
              padding: "4px 12px",
              borderRadius: "6px",
              backgroundColor: withOpacity(theme.primaryColor, "0.06"),
              display: "flex",
              alignItems: "center",
              gap: "4px",
              opacity: 0.8,
            }}
          >
            <Icon name="reply" style={{ color: theme.primaryColor, fontSize: "12px", margin: 0 }} />
            <AppText.FootnoteSmall color={theme.primaryColor} fontWeight={500}>
              {t("chat.reply_in_thread")}
            </AppText.FootnoteSmall>
          </div>
        </div>
      )}
    </div>
  );
};
